/*
 * Rod Sleeve "Minimal (FL-5)" (section setting Deal flow = "Minimal (FL-5)"; template product.rod-sleeve-pro-1, the main design).
 * Source: Curtis's preferred fork of FL-5 Minimal % flat, CLAUDE-APP/docs/prototypes/fl5-claude/fl5-claude.html (2026-10-16),
 * on the site's cream card with LureShield's square ADD TO CART (Curtis: "make it our colour background on the website, and the
 * add to cart button square like the LureShield").
 *
 * Behaviour (as the fork):
 * - opens empty with "Single sleeve" ticked (st.pend = 1; F4, Curtis 2026-10-19, was 4+). Nothing is in the order until a
 *   rod length is picked (st.pick).
 * - picking a length with an empty order puts the ticked deal on it (4+ gives 4 × that length; Single gives 1). With one length
 *   in the order, a new pick moves every sleeve onto it. A mix the shopper built stays as it is.
 * - a deal tap before a length is picked waits in st.pend: the length step asks ("Pick your rod length first…") and is
 *   brought into view. With a length: one length in the order → the whole deal on the picked length ("8 × 6–7 ft"); a mix →
 *   topped up on the picked length, or trimmed off the biggest count so the mix keeps its shape.
 * - "Your sleeves" (the rows) shows once a length is picked and a multi-sleeve deal (4+ / 6+ / 8+) is ticked: a drawer
 *   that drops down on that deal and shuts again on Single sleeve (F4). The bar never adds before a length.
 * - past a length's stock, a fill spills onto the nearest lengths with stock (as the stepper), so no sleeve is dropped.
 * - each row's count is a typeable field (Curtis 2026-10-17, "Let people type in here"): digits only, committed 300 ms after
 *   typing stops and on blur / Enter, through model.set(), so every total comes from the same order model as − / +.
 *   Clamped to 0…that length's stock (and the order cap); past stock it shows "Max N per length · mix lengths for more".
 *
 * Tier maths: the same functions as assets/ra-nb2-pct.js and assets/ra-nb2-stp.js (pctOf, eachAt, quote, most): from N sleeves
 * every sleeve is P% off, each sleeve's discount rounded down to the cent (Shopify's rule). Integer cents. Display only:
 * cfg.pct.one > 0 is the one preview price. Add to cart adds the real variants (one ra:cart-add event, assets/ra-pdp-cart.js),
 * and Rebuy opens as today. A refused add says why (the stepper's stock-aware messages).
 *
 * Round 1019 (Curtis 2026-10-19, issue #2 B1 / B2 / B9 / B11):
 * - B2, changed by Curtis 2026-10-19: the bar shows whenever the in-page Add to cart is off the screen, above or below
 *   (an IntersectionObserver), on every width, including the first screen as the page loads and with Single sleeve
 *   ticked; it hides while Add to cart is on screen.
 * - B9: Rod Sleeves already in the cart (read from /cart.js on load, after a cart change and on Back) count toward the
 *   tier track, the tier line and the price preview, so 2 in the cart + 2 here shows the 4+ tier as reached. What Add to
 *   cart sends never changes. The sleeves this box just added are not counted twice. If the cart can't be read, the box
 *   counts only its own sleeves (as before).
 * - B1: a sold-out length's chip opens the back-in-stock form (snippets/ra-back-in-stock.liquid) for that length.
 */
(() => {
  if (window.raNb2Min) return;

  window.raNb2Min = (host, cfg) => {
    const q = (s) => host.querySelector(s);
    const qa = (s) => Array.from(host.querySelectorAll(s));
    const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: cfg.currency || 'USD' });
    const $ = (cents) => fmt.format(cents / 100);
    const sl = (n) => `${n} ${n === 1 ? 'sleeve' : 'sleeves'}`;
    const reduced = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollToEl = (node, off) => {
      const y = node.getBoundingClientRect().top + window.pageYOffset - off;
      try {
        window.scrollTo({ top: y, behavior: reduced() ? 'auto' : 'smooth' });
      } catch {
        window.scrollTo(0, y);
      }
    };
    const focus = (node) => {
      if (!node) return;
      try {
        node.focus({ preventScroll: true });
      } catch {
        node.focus();
      }
    };

    /* ---------- tier maths (as assets/ra-nb2-pct.js) ---------- */
    const P = cfg.pct || {};
    const S = cfg.sizes;
    const L = S.length;
    const MAX = cfg.max;
    const ONE = P.one > 0 ? P.one : 0;
    const TIERS = (P.tiers || []).filter((x) => x[0] > 0 && x[1] > 0).sort((a, b) => a[0] - b[0]);
    const TOP = TIERS.length ? TIERS[TIERS.length - 1][1] : 0;
    const sum = (c) => c.reduce((a, b) => a + b, 0);
    const unit = (i) => ONE || S[i].price;
    const pctOf = (n) => TIERS.reduce((p, x) => (n >= x[0] ? x[1] : p), 0);
    const eachAt = (price, pct) => price - Math.floor((price * pct) / 100);
    /* extra: sleeves already in the cart; they set the tier (the store's discount counts every sleeve), not the total */
    const quote = (c, extra = 0) => {
      const n = sum(c);
      const pct = pctOf(n + extra);
      let full = 0;
      let total = 0;
      c.forEach((k, i) => {
        full += k * unit(i);
        total += k * eachAt(unit(i), pct);
      });
      return { n, pct, full, total, save: full - total };
    };
    const most = (i) => (S[i].available ? Math.min(MAX, S[i].most ?? MAX) : 0);
    const zeros = () => S.map(() => 0);
    const FROM = Math.min(...S.map((_, i) => unit(i)));
    const FIRST = TIERS.length ? TIERS[0][0] : 4;

    /* ---------- the order model (no DOM) ---------- */
    /* ac: what this box last sent to the cart ([{ id, quantity }], null if nothing may have landed) */
    /* pend: the ticked deal, Single sleeve (1) by default (F4, Curtis 2026-10-19) */
    const st = { c: zeros(), pick: -1, pend: 1, seen: false, spill: false, busy: '', error: false, why: null, bar: false, ac: null, sent: false };

    /* the order survives Back and reload in this tab (sessionStorage), clamped to today's stock */
    const KEY = `ra-min-${S[0] && S[0].id}`;
    let saveT = 0;
    /* M1 (Curtis 2026-10-19): after a successful add (st.sent), while the box still shows the order it added, nothing is
       saved (the saved order is dropped): a reload then opens a fresh box (Single sleeve) with the cart's sleeves counted,
       not "Add 4 to cart" for the 4 already in the cart. A change after the add is saved again; Back (bfcache) keeps the
       page as is */
    const store = () => {
      try {
        const items = st.sent && model.items();
        if (items && st.ac && items.length === st.ac.length && items.every((x, j) => x.id === st.ac[j].id && x.quantity === st.ac[j].quantity)) {
          window.sessionStorage.removeItem(KEY);
          return;
        }
        window.sessionStorage.setItem(KEY, JSON.stringify({ c: st.c, pick: st.pick, pend: st.pend, ac: st.ac }));
      } catch {
        /* private mode or storage full: the page still works, it just forgets */
      }
    };
    const save = () => {
      clearTimeout(saveT);
      saveT = setTimeout(store, st.busy === 'added' ? 0 : 250); /* M1: the drop right after an add goes at once */
    };
    try {
      const v = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');
      if (v && Array.isArray(v.c) && v.c.length === L && Number.isInteger(v.pick) && v.pick >= 0 && v.pick < L && most(v.pick) > 0) {
        let room = MAX;
        st.c = v.c.map((k, i) => {
          const n = Math.max(0, Math.min(Math.floor(+k) || 0, most(i), room));
          room -= n;
          return n;
        });
        st.pick = v.pick;
        st.seen = true;
        if ([1].concat(TIERS.map((x) => x[0])).includes(v.pend)) st.pend = v.pend;
        /* an order saved before F4 (or by an older page) can hold several sleeves with Single sleeve ticked: tick the
           tier those sleeves reach (the first tier below it), so the "Your sleeves" drawer opens on them */
        const had = sum(st.c);
        if (st.pend === 1 && had > 1) st.pend = TIERS.reduce((a, x) => (had >= x[0] ? x[0] : a), FIRST);
        if (Array.isArray(v.ac) && v.ac.every((x) => x && Number.isInteger(x.id) && Number.isInteger(x.quantity))) st.ac = v.ac;
      }
    } catch {
      /* no saved order */
    }

    const used = (c) => c.filter(Boolean).length;
    /* t sleeves from `at`: extra ones on `at`, then the nearest lengths with stock (size up first on a tie) */
    const topUp = (c, t, at) => {
      const out = c.slice();
      let d = t - sum(out);
      const near = S.map((_, i) => i).sort((a, b) => Math.abs(a - at) - Math.abs(b - at) || b - a);
      for (const i of near) {
        if (d <= 0) break;
        const k = Math.min(d, Math.max(0, most(i) - out[i]));
        out[i] += k;
        d -= k;
      }
      st.spill = out[at] - c[at] < t - sum(c);
      return out;
    };
    /* fewer: off the biggest count (the fork's rule), so the shopper's mix keeps its shape */
    const trim = (c, t) => {
      const out = c.slice();
      while (sum(out) > t) {
        let k = -1;
        out.forEach((x, i) => {
          if (x > 0 && (k < 0 || x > out[k])) k = i;
        });
        out[k]--;
      }
      return out;
    };
    const fill = (t, at) => topUp(zeros(), Math.min(t, MAX), at);

    const model = {
      quote: () => quote(st.c),
      /* what Add to cart sends: the real variants, or null with nothing chosen */
      items: () => (sum(st.c) ? st.c.map((quantity, i) => ({ id: S[i].id, quantity })).filter((x) => x.quantity > 0) : null),
      /* the length step. Empty order: the ticked deal on it. One length: everything moves onto it. A mix: left as it is */
      pick: (i) => {
        if (most(i) <= 0) return 'none';
        const n = sum(st.c);
        st.spill = false;
        st.pick = i;
        st.seen = true;
        if (!n) st.c = fill(st.pend, i);
        else if (used(st.c) <= 1) st.c = fill(n, i);
        return 'set';
      },
      /* a deal: before a length, it waits ('ask'); else as the header says */
      tier: (t) => {
        st.spill = false;
        st.pend = t;
        /* F13: before a length, Single asks for the size; 4+ / 6+ / 8+ open "Your sleeves" at 0 for a mix */
        if (st.pick < 0) return t === 1 ? 'ask' : 'mix';
        const c = st.c;
        const n = sum(c);
        if (used(c) <= 1 || t === 1) st.c = fill(t, st.pick);
        else if (t > n) st.c = topUp(c, Math.min(t, MAX), st.pick);
        else st.c = trim(c, t);
        return 'set';
      },
      /* one length's − / + */
      row: (i, d) => {
        st.spill = false;
        if (d > 0) {
          if (sum(st.c) < MAX && st.c[i] < most(i)) st.c[i]++;
          if (st.pick < 0) st.pick = i;
          st.seen = true;
        } else if (st.c[i]) st.c[i]--;
        return 'set';
      },
      /* a typed count: clamped to 0…stock and the order cap. Past stock, st.spill shows the "Max N per length" line */
      set: (i, v) => {
        const room = Math.min(most(i), MAX - (sum(st.c) - st.c[i]));
        const k = Math.max(0, Math.min(v, room));
        st.spill = v > room;
        st.c[i] = k;
        if (k > 0) {
          if (st.pick < 0) st.pick = i;
          st.seen = true;
        }
        return 'set';
      },
      /* which radio is ticked: the waiting deal at 0, Single at 1, nothing at 2–3, else the tier reached */
      /* F17: the tier this box + the cart (E) reach; below the first tier, the deal being built toward (F13) */
      lit: (E = 0) => {
        const n = sum(st.c);
        const got = TIERS.reduce((a, x) => (n + E >= x[0] ? x[0] : a), 0);
        /* F17 (Curtis 2026-10-19): nothing in the box yet, the cart's own tier is ticked (a deal tapped above it stays) */
        if (!n) return Math.max(st.pend, got);
        if (got) return got;
        return st.pend > 1 ? st.pend : n === 1 ? 1 : 0;
      },
    };

    /* ---------- the layout ---------- */
    const el = {
      len: q('[data-len]'),
      chips: qa('[data-pick]'),
      ask: q('[data-ask]'),
      rungs: qa('[data-tier]'),
      pane: q('[data-pane]'),
      mix: q('[data-mix]'),
      qh: q('[data-qh]'),
      segs: qa('[data-seg]'),
      tt: q('[data-tt]'),
      rows: qa('[data-row]'),
      hint: q('[data-hint]'),
      fit: q('[data-fit]'),
      cnt: q('[data-cnt]'),
      was: q('[data-was]'),
      total: q('[data-total]'),
      dl: q('[data-dl]'),
      inline: q('[data-atc-inline]'),
      errs: qa('[data-err]'),
      bar: host.querySelector('[data-pbar]'),
      barL: host.querySelector('[data-pbar-l]'),
      barT: host.querySelector('[data-pbar-t]'),
      barD: host.querySelector('[data-pbar-d]'),
      barBtn: host.querySelector('[data-pbar-btn]'),
      incart: q('[data-incart]'),
      bis: q('[data-bis]'),
    };
    const title = el.cnt ? el.cnt.textContent : 'Rod Sleeve Pro';
    const barName = (el.bar && el.bar.dataset.name) || 'Rod Sleeve';
    const rowEls = el.rows.map((r, i) => ({
      count: r.querySelector('[data-count]'),
      dec: r.querySelector('[data-act="dec"]'),
      inc: r.querySelector('[data-act="inc"]'),
      lab: S[i].label,
    }));
    const rc = el.rungs.map((b) => b.querySelector('[data-rc]'));
    const errText = el.errs.map((e) => e.textContent);

    const txt = (node, s) => {
      if (node && node.textContent !== s) node.textContent = s;
    };
    const attr = (node, k, v) => {
      if (node && node.getAttribute(k) !== v) node.setAttribute(k, v);
    };
    const prop = (node, k, v) => {
      if (node && node[k] !== v) node[k] = v;
    };
    /* F17: the bar's "Add 12 to cart · $135.00"; " to cart ·" is a span the shared CSS drops under 360px, where the
       button (19px, kept for contrast) then reads "Add 12" over "$135.00" instead of breaking mid-phrase */
    const barText = (node, s) => {
      if (!node || node.dataset.k === s) return;
      node.dataset.k = s;
      const m = /^(Add \d+(?: more)?)( to cart ·)( .+)$/.exec(s);
      /* review (f): under 360px " to cart ·" is hidden, so the button keeps its whole label as its name */
      if (m) node.setAttribute('aria-label', s);
      else node.removeAttribute('aria-label');
      if (!m) return void (node.textContent = s);
      node.textContent = '';
      /* one wrapper: the button is a flex box, so loose text and the span would each become a flex item */
      const w = document.createElement('span');
      /* review 1019: "Add 12 to cart ·" never breaks inside itself (360-399px read "Add 12 to / cart · $135.00"); a
         break comes only before the price */
      const h = document.createElement('span');
      h.className = 'ra-sbar__nw';
      const v = document.createElement('span');
      v.className = 'ra-sbar__x';
      v.textContent = m[2];
      h.append(m[1], v);
      w.append(h, m[3]);
      node.append(w);
    };
    /* a visually hidden word in front of a price ("was", "total"), from text nodes */
    const money = (node, word, s) => {
      const key = `${word}|${s}`;
      if (!node || node.dataset.k === key) return;
      node.dataset.k = key;
      node.textContent = '';
      if (!s) return;
      if (word) {
        const v = document.createElement('span');
        v.className = 'nb2-sr';
        v.textContent = `${word} `;
        node.append(v);
      }
      node.append(s);
    };

    /* ---------- B9: Rod Sleeves already in the cart ---------- */
    let hold = false; /* keep the cart line's room (set at load when a length is already picked) */
    let carted = 0; /* every length of this product in the cart, from the last /cart.js read (0 until read, or if it fails) */
    /* in the cart beyond what this box just sent: while the box still shows the order it added, those sleeves are in the
       cart already and are not counted twice (never more than the cart really has, so the deal is never overstated) */
    const extra = () => {
      const items = model.items();
      const same = !!(items && st.ac && items.length === st.ac.length && items.every((x, j) => x.id === st.ac[j].id && x.quantity === st.ac[j].quantity));
      return Math.max(0, carted - (same ? sum(st.c) : 0));
    };

    const render = () => {
      const c = st.c;
      const E = extra();
      const Q = quote(c, E);
      const n = Q.n;
      const N = n + E; /* the tier count: this box + the cart */
      const lit = model.lit(E);
      const open = st.pend > 1; /* F4 / F13: "Your sleeves" is open on 4+ / 6+ / 8+, with or without a length */
      const next = TIERS.find((x) => x[0] > N);
      const nopick = st.pick < 0;

      /* 1. the rod length */
      el.chips.forEach((b, i) => {
        const on = i === st.pick;
        attr(b, 'aria-checked', on ? 'true' : 'false');
        b.classList.toggle('is-on', on);
      });
      const tabChip = el.chips[st.pick] || el.chips.find((b, i) => !b.disabled && most(i) > 0) || el.chips.find((b) => !b.disabled);
      el.chips.forEach((b) => prop(b, 'tabIndex', b === tabChip ? 0 : -1));
      if (!nopick || open) prop(el.ask, 'hidden', true);

      /* 2. the deal list */
      let tab = null;
      el.rungs.forEach((b, j) => {
        const k = +b.dataset.tier;
        const on = k === lit;
        attr(b, 'aria-checked', on ? 'true' : 'false');
        txt(rc[j], n && on && k > 1 && N !== k ? (E ? `${n} + ${E} in cart` : sl(n)) : '');
        if (on) tab = b;
      });
      el.rungs.forEach((b) => prop(b, 'tabIndex', (tab ? b === tab : +b.dataset.tier === FIRST) ? 0 : -1));

      /* your sleeves: shown once a length is picked. The tier track, the tier line and the cart line too (B11: before a
         pick the deal list above already says the tiers, so they are not said twice; nothing here moves on load) */
      el.pane.classList.toggle('is-nopick', nopick && !open);
      /* F4: the "Your sleeves" drawer is shut on Single sleeve (the last deal tapped, or the default) */
      el.pane.classList.toggle('is-single', !open);
      prop(el.mix, 'inert', !open);
      el.segs.forEach((s, j) => {
        const lo = j ? TIERS[j - 1][0] : 0;
        const hi = TIERS[j][0];
        const f = Math.max(0, Math.min(1, (N - lo) / (hi - lo)));
        const u = s.querySelector('u');
        const tf = `scaleX(${f})`;
        if (u.style.transform !== tf) u.style.transform = tf;
        s.className = (N >= hi ? 'is-on' : '') + (Q.pct === TIERS[j][1] ? ' is-cur' : '');
      });
      el.tt.classList.toggle('is-won', Q.pct > 0);
      el.tt.classList.toggle('is-near', N > 0 && !Q.pct);
      txt(
        el.tt,
        !N
          ? `Buy ${FIRST} or more for ${TIERS.length ? TIERS[0][1] : 0}% off`
          : !Q.pct
            ? `${next[0] - N} more for ${next[1]}% off`
            : next
              ? `✓ ${Q.pct}% off · ${next[0] - N} more for ${next[1]}% off`
              : `✓ ${Q.pct}% off, the best deal`,
      );
      if (el.incart) {
        /* review: with a length restored at load the pane shows before the cart read; its line keeps its room (empty)
           until the shopper first acts, so the read never pushes the rows down */
        prop(el.incart, 'hidden', !E && !hold);
        el.incart.classList.toggle('is-held', !E && hold);
        txt(el.incart, E ? `Counts the ${sl(E)} already in your cart` : '');
      }
      el.rows.forEach((r, i) => {
        const R = rowEls[i];
        const k = c[i];
        r.classList.toggle('is-zero', !k);
        /* the typed field: left alone while its own typing waits to commit */
        if (R.count.value !== String(k) && !(R.count === document.activeElement && typing)) R.count.value = String(k);
        prop(R.dec, 'disabled', !k);
        prop(R.inc, 'disabled', n >= MAX || k >= most(i));
        attr(R.dec, 'aria-label', `One fewer ${R.lab}, ${k} chosen`);
        attr(R.inc, 'aria-label', `One more ${R.lab}, ${k} chosen`);
      });
      /* at a length's stock: say why + stopped. Review 1019: when the cart's sleeves already reach a higher tier than the
         deal tapped, the hint drops "Pick N" (the tick shows the reached tier; N would name a second one). Final review
         (e): also when the cart's sleeves exactly reach the tapped deal (8 in the cart, 8+ tapped) */
      const capped = st.pick >= 0 && ((st.spill && n > 0) || c.some((k, i) => k > 0 && k >= most(i) && most(i) < MAX));
      const cap = st.pick >= 0 ? Math.min(...c.map((k, i) => (k > 0 && k >= most(i) ? most(i) : MAX))) : MAX;
      txt(el.hint, capped ? `Max ${cap} per length · mix lengths for more` : open && !n ? (lit > st.pend || E >= st.pend ? 'Tap + on the lengths you need' : `Pick ${st.pend} sleeves: tap + on the lengths you need`) : '');

      /* Curtis 2026-10-22: once the box's order is in the cart (after "Added ✓", the box still shows it), the summary says
         what the CART holds, at the deal the cart really gets ("In your cart: 4 sleeves · 15% off · $51.00"), so it can't
         be read as a second price beside the next-add button. Every sleeve is cart-counted (all lengths of this product);
         the total is exact while every length costs the same, else it is shown only when the cart is just this order. */
      const inCartNow = n > 0 && carted > E && !st.busy;
      const cartPct = pctOf(carted);
      const uniform = S.every((_, i) => unit(i) === FROM);
      const cartFull = uniform ? carted * FROM : E === 0 ? Q.full : null;
      const cartTotal = uniform ? carted * eachAt(FROM, cartPct) : E === 0 ? Q.total : null;
      const cartNext = TIERS.find((x) => x[0] > carted);

      /* the summary */
      txt(
        el.cnt,
        inCartNow
          ? `In your cart: ${sl(carted)}${cartPct ? ` · ${cartPct}% off` : ''}`
          : n
            ? `${sl(n)}${Q.pct ? ` · ${Q.pct}% off` : ''}`
            : lit === 1 && !nopick
              ? 'Single sleeve'
              : title,
      );
      money(el.was, 'was', inCartNow ? (cartPct && cartFull != null ? $(cartFull) : '') : n && Q.pct ? $(Q.full) : '');
      /* F17: before the box holds a sleeve, the price a sleeve costs at the cart's tier ("From $11.25 each") */
      const from = Q.pct ? `From ${$(eachAt(FROM, Q.pct))} each` : `From ${$(FROM)}`;
      money(el.total, inCartNow ? (cartTotal != null ? 'cart total' : '') : n ? 'total' : '', inCartNow ? (cartTotal != null ? $(cartTotal) : '') : n ? $(Q.total) : from);
      el.dl.className = `nb2-mdl${!n ? ' is-pick' : (inCartNow ? cartPct : Q.pct) ? ' is-won' : ' is-near'}`;
      txt(
        el.dl,
        !n
          ? nopick && !open
            ? 'Pick your rod length above'
            : 'Tap + on the lengths you need'
          : inCartNow
            ? cartPct && cartFull != null && cartTotal != null
              ? `You save ${$(cartFull - cartTotal)} (${cartPct}% off)`
              : cartNext
                ? `Add ${cartNext[0] - carted} more for ${cartNext[1]}% off`
                : 'Full price'
            : Q.pct
              ? `You save ${$(Q.save)} (${Q.pct}% off)`
              : next
                ? `Add ${next[0] - N} more for ${next[1]}% off`
                : 'Full price',
      );

      /* Review 2026-10-21 (Codex): once "Added ✓" clears, the box still shows the order it just added (not counted
         twice above, M1), but its button offers to add that order AGAIN. Those sleeves would join the ones now in the
         cart, so the button quotes them with the whole cart counted ("Add 4 more to cart · $45.00", the 8+ deal) instead of
         the first add's price ($51.00). */
      const again = n > 0 && carted > E;
      const label =
        st.busy === 'adding'
          ? 'Adding…'
          : st.busy === 'checking'
            ? 'Checking…'
            : st.busy === 'added'
              ? 'Added ✓'
              : n
                ? again
                  ? `Add ${n} more to cart · ${$(quote(c, carted).total)}`
                  : `Add ${n} to cart · ${$(Q.total)}`
                : nopick
                  ? 'Choose your rod length'
                  : 'Choose your size';
      /* review pass 4: after a partial or uncertain add the inline button opens the cart too (as the bar's does), and says so */
      const inCart = st.error && !st.busy && st.why && st.why.cart;
      txt(el.inline, inCart ? 'Check your cart' : label);
      attr(el.inline, 'aria-busy', st.busy === 'adding' || st.busy === 'checking' ? 'true' : 'false');
      el.errs.forEach((e, j) => {
        prop(e, 'hidden', !st.error);
        txt(e, (st.error && st.why && st.why.msg) || errText[j]);
      });

      /* the bar */
      if (el.bar) {
        el.bar.classList.toggle('is-pre', !n && !st.error);
        el.bar.classList.toggle('is-err', st.error);
        /* F11 (2026-10-19), the shared bar: "2 Rod Sleeves · $26.98" (the price in #BE400C) over "✓ 10% off unlocked" in
           green; after a failed add "Couldn't add to cart" over the reason in red */
        if (st.error) {
          txt(el.barL, "Couldn't add to cart");
          txt(el.barT, '');
          txt(el.barD, (st.why && st.why.short) || 'Please try again');
        } else {
          /* K4 + K5: none in the box yet: just "From $14.99" (no name); then "Rod Sleeve · $26.98" over the count and
             the deal, "2 sleeves · ✓ 10% off" ("✓ 10% off unlocked" for one sleeve at the cart's tier) */
          txt(el.barL, n ? barName : '');
          txt(el.barT, n ? $(Q.total) : from);
          txt(el.barD, n > 1 ? `${sl(n)}${Q.pct ? ` · ✓ ${Q.pct}% off` : ''}` : n && Q.pct ? `✓ ${Q.pct}% off unlocked` : '');
        }
        /* review pass 3: after a partial or uncertain add (st.why.cart) part of it is in the cart, so the bar's button opens
           the cart instead of offering a retry (a retry re-added the length that landed: 1 became 2). K4: with nothing
           in the box the bar says "Choose length" (the in-page button keeps the longer wording) */
        barText(el.barBtn, st.error && !st.busy ? (inCart ? 'Check your cart' : 'Try again') : !n && !st.busy ? 'Choose length' : label);
      }
      if (rendered) save();
      rendered = true;
      sync();
    };
    let rendered = false;

    /* why Shopify refused an add, in words (as assets/ra-nb2-stp.js): the page caps each length at its stock, but not what
       is already in the cart, so on a 422 read the cart (a GET) and name the length */
    const why = async (err, items) => {
      const status = err && err.status;
      /* cart: true = the add landed, or may have, in whole or in part (the bar's button then opens the cart) */
      if (err && err.uncertain) return { msg: 'The connection is slow, so we could not confirm the add. Check your cart before adding again.', short: 'Check your cart', cart: true };
      if (err && err.offline) return { msg: err.message, short: "You're offline" };
      if (status === 429) return { msg: 'The store is busy right now. Wait a few seconds, then try again.', short: 'Wait, then try again' };
      if (status !== 422) return null;
      /* a 422: Shopify added what it could, and assets/ra-pdp-cart.js read the cart after (err.cart; null if it could not).
         So the cart below already holds this add's part: say what is in it, never "try again" (that would add it twice) */
      const have = {};
      let cart = err && 'cart' in err ? err.cart : undefined;
      if (cart === undefined) {
        try {
          const base = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
          cart = await (await fetch(`${base}.js`, { headers: { Accept: 'application/json' } })).json();
        } catch {
          cart = null;
        }
      }
      ((cart && cart.items) || []).forEach((it) => (have[it.variant_id] = (have[it.variant_id] || 0) + it.quantity));
      const out = [];
      let rest = false;
      if (cart) {
        items.forEach((x) => {
          const i = S.findIndex((v) => v.id === x.id);
          if (i < 0) return;
          const left = most(i);
          const inCart = have[x.id] || 0;
          /* short of what was asked, or at the length's tracked stock: this is the length Shopify stopped at */
          if (inCart < x.quantity || (left < MAX && inCart >= left)) {
            const lab = S[i].label;
            out.push(inCart ? `Only ${inCart} of ${lab} in stock, so your cart now has ${inCart}.` : `${lab} is out of stock.`);
          } else rest = true;
        });
      }
      if (out.length) return { msg: `${out.join(' ')}${rest ? ' The rest is in your cart.' : ''}`, short: 'Not enough stock', cart: true };
      const said = String((err && err.description) || '').trim();
      return { msg: `${said ? `${said} ` : ''}Check your cart before adding again.`, short: 'Check your cart', cart: true };
    };

    /* ---------- actions ---------- */
    /* "Pick your rod length first": the line, a short nudge on the chips, and the length step brought into view */
    const askLen = () => {
      prop(el.ask, 'hidden', false);
      el.len.classList.remove('is-nudge');
      requestAnimationFrame(() => el.len.classList.add('is-nudge'));
      scrollToEl(el.len, 12);
      focus(el.chips.find((b, i) => !b.disabled && most(i) > 0));
    };
    /* the sleeves rows into view, once (a later tap only moves the page when forced: Add to cart / the bar at 0) */
    /* F13: the + to focus in the drawer: the picked length's, else the first length with stock */
    const firstInc = () => {
      const ok = (i) => i >= 0 && most(i) > 0 && !rowEls[i].inc.disabled;
      const j = ok(st.pick) ? st.pick : rowEls.findIndex((_, i) => ok(i));
      return j >= 0 ? rowEls[j].inc : null;
    };
    const toSizes = (force) => {
      if (st.pend === 1) return st.pick < 0 ? askLen() : undefined; /* F4: Single sleeve: the rows are shut */
      if (st.seen && !force) return;
      st.seen = true;
      scrollToEl(el.qh, 12);
      focus(firstInc() || el.qh);
    };
    let flashT = 0;
    const flash = (before) => {
      el.rows.forEach((r, i) => r.classList.toggle('is-ch', before[i] !== st.c[i]));
      clearTimeout(flashT);
      flashT = setTimeout(() => el.rows.forEach((r) => r.classList.remove('is-ch')), 900);
    };

    let doneT = 0;
    const add = async () => {
      const items = model.items();
      if (st.busy || !items) return;
      st.busy = 'adding';
      st.error = false;
      st.why = null;
      st.sent = false; /* M1 */
      render();
      try {
        /* review M2 (2026-10-18): the button stays busy until the add settles. assets/ra-pdp-cart.js time-limits every
           request, so it always does; after 15 s the label says "Checking…". Only an add nobody heard (detail.accepted
           unset: the cart module is missing, nothing was sent) is given up after 15 s. */
        let slow = 0;
        await new Promise((resolve, reject) => {
          const detail = {
            items,
            source: 'ra-nb2-buybox',
            resolve: (v) => (clearTimeout(slow), resolve(v)),
            reject: (e) => (clearTimeout(slow), reject(e)),
          };
          host.dispatchEvent(new CustomEvent('ra:cart-add', { bubbles: true, detail }));
          slow = setTimeout(() => {
            if (!detail.accepted) return reject(new Error('timeout'));
            st.busy = 'checking';
            render();
          }, 15000);
        });
        st.busy = 'added';
        st.ac = items; /* B9: these are in the cart now; not counted twice while the box still shows them */
        st.sent = true; /* M1: render() saves, and store() drops the saved order while the box shows what it added */
        render();
        clearTimeout(doneT);
        doneT = setTimeout(() => {
          st.busy = '';
          render();
        }, 1600);
      } catch (err) {
        st.busy = '';
        st.error = true;
        try {
          st.why = await why(err, items);
        } catch {
          st.why = null;
        }
        /* B9: part of it may be in the cart (a partial or uncertain add): count it as landed, so the deal is never overstated */
        if (st.why && st.why.cart) st.ac = items;
        render();
      }
    };
    const buy = () => {
      if (sum(st.c)) add();
      else if (st.pick >= 0 && st.pend === 1) {
        /* F4: Single sleeve with an empty order (a restored order whose lengths sold out): one of the picked length,
           and the tap adds it, as any other Add to cart tap does */
        model.pick(st.pick);
        render();
        if (sum(st.c)) add();
      } else toSizes(true);
    };
    /* the cart drawer, as the header's cart link opens it (assets/ra-cart-rebuy.js; it falls back to the cart page) */
    const openCart = () => {
      const link = document.querySelector('a.action__cart, a[data-ra-cart-open]');
      if (link) link.click();
      else window.location.assign((window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart');
    };

    let kbd = false; /* F13: set while an arrow key moves the radio choice */
    const onClick = (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      hold = false;
      let x;
      if ((x = t.closest('[data-pick]'))) {
        if (x.disabled) return;
        /* B1: a sold-out length opens the back-in-stock form for it */
        if (most(+x.dataset.pick) <= 0) return bis(+x.dataset.pick);
        bis(-1);
        const before = st.c.slice();
        model.pick(+x.dataset.pick);
        st.error = false;
        render();
        flash(before);
        return;
      }
      if (t.closest('[data-fitlink]')) {
        e.preventDefault();
        openFit(true);
        return;
      }
      if ((x = t.closest('[data-tier]'))) {
        const before = st.c.slice();
        const r = model.tier(+x.dataset.tier);
        st.error = false;
        render();
        /* F13: a keyboard move through the radios never scrolls the page or takes focus off the radio */
        if (r === 'ask') {
          if (kbd) prop(el.ask, 'hidden', false);
          else askLen();
          return;
        }
        flash(before);
        if (st.pend > 1 && !kbd) toSizes(true);
        return;
      }
      if ((x = t.closest('[data-act]'))) {
        if (x.disabled) return;
        const i = +x.dataset.i;
        const had = document.activeElement === x;
        model.row(i, x.dataset.act === 'inc' ? 1 : -1);
        st.error = false;
        render();
        /* a button that disabled itself: keep focus on the row */
        if (had && x.disabled) focus(x.dataset.act === 'dec' ? rowEls[i].inc : rowEls[i].dec);
        return;
      }
      /* review pass 4 (2026-10-19): the inline Add to cart too, not only the bar (its retry re-added the length that landed: 1 became 2) */
      if (t.closest('[data-atc-inline], [data-pbar-btn]') && st.error && !st.busy && st.why && st.why.cart) return openCart();
      if (t.closest('[data-atc-inline], [data-pbar-btn]')) buy();
    };
    host.addEventListener('click', onClick);

    /* the typed count: digits only; commit 300 ms after typing stops, and on blur / Enter. Empty or invalid on blur = 0 */
    let typing = 0;
    const field = (t) => (t instanceof HTMLInputElement && t.matches('[data-count]') ? t : null);
    const commit = (f, blank0) => {
      clearTimeout(typing);
      typing = 0;
      const raw = f.value.replace(/\D/g, '');
      if (!raw && !blank0) return;
      const i = +f.dataset.i;
      const v = raw ? parseInt(raw, 10) : 0;
      if (v === st.c[i] && String(v) === f.value) return;
      model.set(i, v);
      st.error = false;
      render();
      prop(f, 'value', String(st.c[i]));
    };
    const onInput = (e) => {
      const f = field(e.target);
      if (!f) return;
      hold = false;
      fresh = null;
      typed = true;
      const d = f.value.replace(/\D/g, '');
      if (d !== f.value) f.value = d;
      clearTimeout(typing);
      typing = setTimeout(() => commit(f, false), 300);
    };
    /* typing replaces the number: select it on focus, and again on the click that focused it (a mouse-up or tap would
       otherwise drop the selection to a caret) */
    let fresh = null;
    let typed = false;
    const all = (f) => {
      try {
        f.setSelectionRange(0, f.value.length);
      } catch {
        f.select();
      }
    };
    const onFocusIn = (e) => {
      const f = field(e.target);
      if (!f) return;
      fresh = f;
      typed = false;
      all(f);
    };
    /* Chrome moves the caret on the mouse-up of the tap that focused an already-selected field: keep the selection */
    const onFieldUp = (e) => {
      const f = field(e.target);
      if (f && f === fresh) e.preventDefault();
    };
    const onFieldClick = (e) => {
      const f = field(e.target);
      if (!f || f !== fresh) return;
      fresh = null;
      all(f);
      /* and once more after the tap, unless typing has started */
      setTimeout(() => {
        if (document.activeElement === f && !typed) all(f);
      }, 0);
    };
    const onFocusOut = (e) => {
      const f = field(e.target);
      if (!f) return;
      fresh = null;
      commit(f, true);
      geo = true; /* the phone keypad has closed: measure again */
      kick();
    };
    const onFieldKey = (e) => {
      const f = field(e.target);
      if (!f || e.key !== 'Enter') return;
      e.preventDefault();
      commit(f, true);
      f.select();
    };
    host.addEventListener('input', onInput);
    host.addEventListener('focusin', onFocusIn);
    host.addEventListener('click', onFieldClick);
    host.addEventListener('mouseup', onFieldUp);
    host.addEventListener('focusout', onFocusOut);
    host.addEventListener('keydown', onFieldKey);

    /* radio pattern: arrows move through the lengths and the deals and choose them */
    const onKey = (e) => {
      const k = e.key;
      const dir = k === 'ArrowRight' || k === 'ArrowDown' ? 1 : k === 'ArrowLeft' || k === 'ArrowUp' ? -1 : 0;
      if (!dir || !(e.target instanceof Element)) return;
      hold = false;
      /* Review 2026-10-21 (Codex): sold-out lengths (aria-disabled, B1) are in the arrow order too, so keyboard users can
         reach their back-in-stock form. An arrow only moves focus onto one; Enter or Space opens the form, as a tap does. */
      const group = e.target.closest('[data-tier]') ? el.rungs : e.target.closest('[data-pick]') ? el.chips.filter((b) => !b.disabled) : null;
      if (!group) return;
      let i = group.indexOf(e.target);
      if (i < 0) return;
      e.preventDefault();
      i = (i + dir + group.length) % group.length;
      group[i].focus();
      if (group[i].getAttribute('aria-disabled') === 'true') return;
      kbd = true;
      try {
        group[i].click();
      } finally {
        kbd = false;
      }
    };
    host.addEventListener('keydown', onKey);

    /* the bar (Curtis 2026-10-19, replaces B2): on, at every width, whenever the in-page Add to cart is not on the screen,
       above it or still below it (so on the first screen as the page loads, and with Single sleeve ticked); away while
       any of it is on screen. An IntersectionObserver says when; without one, a scroll / resize check does. The bar is
       fixed and slides in with transform: no layout shift. */
    let raf = 0;
    let above = false;
    let io = null;
    let geo = true; /* re-measure Add to cart on the next frame (a scroll or resize happened) */
    const cartOpen = () => {
      try {
        const b = document.body;
        if (document.documentElement.classList.contains('rebuy-cart-visible')) return true;
        if (b && b.classList.contains('rebuy-cart-visible')) return true;
        const rc = document.getElementById('rebuy-cart');
        return !!(rc && (rc.getAttribute('aria-hidden') === 'false' || rc.classList.contains('is-visible')));
      } catch {
        return false; /* a check that fails never keeps the bar down */
      }
    };
    const past = () => {
      const r = el.inline.getBoundingClientRect();
      return r.height > 0 && (r.bottom <= 0 || r.top >= (window.innerHeight || document.documentElement.clientHeight));
    };
    function sync() {
      raf = 0;
      if (!el.bar) return;
      /* while a count field is being typed in, the bar stays as it is: the phone keypad shrinking the viewport must not move anything */
      if (field(document.activeElement) && host.contains(document.activeElement)) return;
      /* review (2026-10-19): the observer only calls back when Add to cart crosses the edge, and a jump (a link, End, a
         status-bar tap to the top) can carry it from below the screen to above in one frame with no call: every scroll
         and resize re-measures too, once per frame */
      if (geo || !io) above = past();
      geo = false;
      /* down while the Rebuy cart drawer is open, as the shared bar (assets/ra-sbar.js) */
      const on = above && !cartOpen();
      if (on === st.bar) return;
      st.bar = on;
      el.bar.classList.toggle('is-on', on);
      attr(el.bar, 'aria-hidden', on ? 'false' : 'true');
      prop(el.barBtn, 'tabIndex', on ? 0 : -1);
      /* room under the footer while the bar shows, and Tab keeps focus above it (ra-nb2-more.css) */
      document.documentElement.classList.toggle('ra-nb2-bar-on', on);
      if (on) document.documentElement.style.setProperty('--nb2-bar-room', `${el.bar.offsetHeight + 16}px`);
    }
    const kick = () => {
      if (!raf) raf = requestAnimationFrame(sync);
    };
    try {
      if (el.bar && el.inline && typeof window.IntersectionObserver === 'function') {
        io = new window.IntersectionObserver((list) => {
          const e = list[list.length - 1];
          above = !e.isIntersecting && e.boundingClientRect.height > 0;
          geo = false;
          kick();
        });
        io.observe(el.inline);
      }
    } catch {
      io = null;
    }
    const moved = () => {
      geo = true;
      kick();
    };
    window.addEventListener('scroll', moved, { passive: true });
    window.addEventListener('resize', moved, { passive: true });
    try {
      if (el.bar && typeof window.MutationObserver === 'function') {
        const mo = new window.MutationObserver(kick);
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        if (document.body) mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      }
    } catch {
      /* the next scroll re-checks */
    }

    /* B1: the back-in-stock form (snippets/ra-back-in-stock.liquid) under the lengths, for the sold-out length tapped
       (i < 0 closes it). v2 (Curtis 2026-10-19, tick box): assets/ra-bis.js sends it as a tagged Shopify customer
       (ticked) or a contact-form message to the store (unticked); this writes the length into the heading, the message
       and the tags ("back-in-stock, bis-rod-sleeve, bis-rod-sleeve-7-8ft", from the chip's data-bis-tag). */
    const bisH = el.bis && el.bis.querySelector('[data-bis-h]');
    const bisBody = el.bis && el.bis.querySelector('[data-bis-body]');
    const bisTags = el.bis && el.bis.querySelector('[data-bis-tags]');
    /* review: after a failed post the form comes back open with the message as sent (form.body, or ra-bis.js's copy,
       then 'bis:restore'); put its length back in the heading, and later sold-out taps still retarget it (only a
       successful post locks it) */
    const bisRestore = () => {
      try {
        const m = bisH && bisBody && !el.bis.hidden && !el.bis.hasAttribute('data-bis-done') ? /\(variant (\d+)\)/.exec(bisBody.value) : null;
        const j = m ? S.findIndex((v) => String(v.id) === m[1]) : -1;
        if (j >= 0) txt(bisH, `Email me when ${S[j].label} is back`);
      } catch {
        /* the heading stays as Shopify rendered it */
      }
    };
    bisRestore();
    try {
      if (el.bis && el.bis.addEventListener) el.bis.addEventListener('bis:restore', bisRestore);
    } catch {
      /* ra-bis.js ran first: bisRestore above already read its copy */
    }
    const bis = (i) => {
      if (!el.bis || el.bis.hasAttribute('data-bis-done')) return;
      if (i < 0 || !S[i]) {
        prop(el.bis, 'hidden', true);
        return;
      }
      const chip = el.chips[i];
      const vt = (chip && chip.getAttribute('data-vt')) || S[i].label;
      txt(bisH, `Email me when ${S[i].label} is back`);
      if (bisBody) bisBody.value = `Back in stock request: ${el.bis.getAttribute('data-bis-product') || title} - ${vt} (variant ${S[i].id})`;
      const tag = chip && chip.getAttribute('data-bis-tag');
      if (bisTags && tag) bisTags.value = `${bisTags.getAttribute('data-bis-base') || 'back-in-stock'}, ${tag}`;
      prop(el.bis, 'hidden', false);
      try {
        el.bis.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
      } catch {
        /* old browsers: it is right under the chips anyway */
      }
    };

    /* B9: count the Rod Sleeves in the cart. One GET /cart.js on load, after the theme's cart update event (once the add or
       change has settled), after a Rebuy drawer change, and on Back (bfcache). Reads are spaced, time-limited and the
       last one wins; a failed read counts 0 (the box as before). Nothing is ever written to the cart from here. */
    const ids = new Set(S.map((v) => String(v.id)));
    let readT = 0;
    let readN = 0;
    const readCart = () => {
      if (typeof window.fetch !== 'function') return;
      const me = ++readN;
      const ctl = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      const stop = setTimeout(() => ctl && ctl.abort(), 8000);
      const base = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
      const done = (k) => {
        clearTimeout(stop);
        if (me !== readN || k === carted) return;
        carted = k;
        render();
      };
      window
        .fetch(`${base}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: ctl ? ctl.signal : undefined })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((cart) => {
          if (!cart || !Array.isArray(cart.items)) throw new Error('no cart');
          done(cart.items.reduce((a, it) => a + (ids.has(String(it.variant_id)) ? Math.max(0, Math.floor(+it.quantity) || 0) : 0), 0));
        })
        .catch(() => done(0));
    };
    const soon = (ms) => {
      clearTimeout(readT);
      readT = setTimeout(readCart, ms);
    };
    const onLines = (e) => {
      soon(10000); /* a fallback, if the promise never settles */
      Promise.resolve(e && e.promise).then(
        () => soon(150),
        () => soon(150),
      );
    };
    const onRebuy = () => soon(800);
    const onShow = (e) => {
      if (e && e.persisted) soon(0);
    };
    try {
      document.addEventListener('shopify:cart:lines-update', onLines);
      document.addEventListener('rebuy:cart.change', onRebuy);
      window.addEventListener('pageshow', onShow);
    } catch {
      /* no events: the load read below still counts */
    }

    /* "Find my size" (menu), "Open Size Guide" (Size Chart row) and "Not sure?" open the Size Guide and centre it */
    const openFit = (moveFocus) => {
      if (!el.fit) return;
      el.fit.open = true;
      try {
        el.fit.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
      } catch {
        el.fit.scrollIntoView();
      }
      if (moveFocus) focus(el.fit.querySelector('summary'));
    };
    const onHash = () => {
      if (window.location.hash !== '#ra-size-guide' || !el.fit) return;
      el.fit.open = true;
      const centre = () => requestAnimationFrame(() => openFit(false));
      if (document.readyState === 'complete') centre();
      else window.addEventListener('load', centre, { once: true });
    };
    onHash();
    window.addEventListener('hashchange', onHash);

    /* ?variant=<id> (ads, catalog links): open on that length, as a tap on its chip; the ticked deal stays as it is.
       Back and reload keep the order saved in this tab instead (the shopper may have changed the length since). */
    try {
      const nav = window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
      const want = new URLSearchParams(window.location.search).get('variant');
      const i = want ? S.findIndex((v) => String(v.id) === want) : -1;
      if (i >= 0 && (!nav || nav.type === 'navigate' || st.pick < 0)) {
        if (most(i) <= 0) bis(i); /* B1: an ad or catalog link to a sold-out length opens its back-in-stock form */
        else {
          model.pick(i);
          store(); /* review pass 3: saved as a tap would be, so a reload keeps this length (not the order saved before) */
        }
      }
    } catch {
      /* no usable ?variant=: the page opens as before */
    }

    hold = st.pick >= 0;
    render();
    readCart();

    return {
      state: st,
      model,
      quote,
      pctOf,
      eachAt,
      render,
      cfg,
      S,
      money: $,
      inline: el.inline,
      openFit,
      extra,
      setCarted: (k) => {
        carted = Math.max(0, Math.floor(+k) || 0);
        render();
      },
      off: () => {
        window.removeEventListener('scroll', moved);
        window.removeEventListener('resize', moved);
        if (io) io.disconnect();
        clearTimeout(readT);
        readN++;
        try {
          document.removeEventListener('shopify:cart:lines-update', onLines);
          document.removeEventListener('rebuy:cart.change', onRebuy);
          window.removeEventListener('pageshow', onShow);
        } catch {
          /* nothing to remove */
        }
        window.removeEventListener('hashchange', onHash);
        host.removeEventListener('click', onClick);
        host.removeEventListener('keydown', onKey);
        host.removeEventListener('input', onInput);
        host.removeEventListener('focusin', onFocusIn);
        host.removeEventListener('click', onFieldClick);
        host.removeEventListener('mouseup', onFieldUp);
        host.removeEventListener('focusout', onFocusOut);
        host.removeEventListener('keydown', onFieldKey);
        clearTimeout(typing);
        clearTimeout(doneT);
        clearTimeout(flashT);
        clearTimeout(saveT);
        store();
        document.documentElement.classList.remove('ra-nb2-bar-on');
      },
    };
  };
})();
